/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  ButtonProps,
  DIALOG_SIZE_MD,
  DIALOG_SIZE_XL,
  DialogContentProps,
  DialogProps,
  ICON_PLAY_CIRCLE_OUTLINE,
} from '@squiz/sds';
import { DialogLauncher } from 'apps/console-core/src/Components/DialogLauncher/DialogLauncher';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { isGenerative } from '../../Utilities/formPredicates';
import { TestTool } from '../TestTool/TestTool';
import { TEST_TOOL_DATA_IDS } from './TestToolDialog.constants';
import { TestToolDialogProps } from './TestToolDialog.props';

/**
 * The button for creating a content tool.
 * @param {TestToolDialogProps} props Props passed to `<TestToolDialog />`.
 * @returns {JSX.Element} The `<TestToolDialog />` component.
 */
export const TestToolDialog = (props: TestToolDialogProps): JSX.Element => {
  const { t } = useTranslation();

  const buttonProps: ButtonProps = {
    classes: 'test-button',
    dataId: TEST_TOOL_DATA_IDS.testButton,
    format: BUTTON_FORMAT_TRANSPARENT,
    icon: ICON_PLAY_CIRCLE_OUTLINE,
    text: t(AI_STRINGS.testTool),
    theme: BUTTON_THEME_DEFAULT,
  };

  const dialogProps: DialogProps = {
    classes: 'test-tool-dialog',
    dialogContent: {
      renderContent: (_dialogProps: DialogContentProps): JSX.Element => {
        _dialogProps.setButtons([
          <Button
            classes="dialog-cancel-btn"
            dataId={TEST_TOOL_DATA_IDS.closeButton}
            format={BUTTON_FORMAT_SOFT}
            key={'cancel-btn'}
            onClick={_dialogProps.close}
            text={t(`Close`)}
            theme={BUTTON_THEME_DEFAULT}
          />,
        ]);

        return <TestTool contentTool={props.contentTool} />;
      },
    },
    dialogSize: isGenerative(props.contentTool.type) ? DIALOG_SIZE_XL : DIALOG_SIZE_MD,
    heading: t(props.title),
    icon: ICON_PLAY_CIRCLE_OUTLINE,
    scrollOnOverflow: false,
    stateHandler: useDialogStore,
  };
  return (
    <div className="test-tool-dialog-launcher">
      <DialogLauncher buttonProps={buttonProps} classes="test-tool-dialog-launcher" dialogProps={dialogProps} />
    </div>
  );
};
